import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { NavLink, useLocation } from "react-router-dom";
import { 
  Home,
  Package, 
  Users, 
  Truck, 
  AlertTriangle,
  MapPin,
  Settings,
  FileText,
  Building2,
  Receipt,
  Car,
  Search,
  Calendar,
  CalendarDays,
  Camera,
  Bell,
  History,
  Map,
  Shield,
  BarChart3,
  Crown,
  Navigation,
  Cake,
  ArrowLeft,
  Clock,
  StickyNote
} from "lucide-react";

export function AdminSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { pathname } = useLocation();

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-destructive/20 bg-gradient-to-b from-destructive/5 to-card backdrop-blur-sm"
      {...props}
    >
      <SidebarHeader className="p-4 border-b border-destructive/20">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-8 h-8 bg-gradient-to-br from-destructive to-destructive/80 rounded-lg flex items-center justify-center shadow-soft">
              <Shield className="h-5 w-5 text-destructive-foreground" />
            </div>
            <div className="absolute -inset-1 bg-gradient-to-br from-destructive to-destructive/80 rounded-lg blur opacity-30 animate-pulse"></div>
          </div>
          <div className="flex flex-col">
            <h2 className="text-sm font-bold bg-gradient-to-r from-destructive to-destructive/80 bg-clip-text text-transparent">
              Admin Panel
            </h2>
            <p className="text-xs text-muted-foreground">Management System</p>
          </div>
        </div>
      </SidebarHeader>
      
      <SidebarContent className="p-2">
        {/* Back to Standard Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton 
                  asChild 
                  className="bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 mb-2"
                >
                  <NavLink 
                    to="/"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                  >
                    <ArrowLeft className="h-5 w-5" />
                    <span className="font-medium">Back to Dashboard</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="px-3 py-2 text-xs font-semibold text-destructive/80 uppercase tracking-wider flex items-center gap-2">
            <Crown className="h-3 w-3" />
            Admin Controls
          </SidebarGroupLabel>
          <SidebarGroupContent className="space-y-1">
            <SidebarMenu>
              {[
                {
                  title: "Dashboard",
                  url: "/admin",
                  icon: Home,
                  isActive: pathname === "/admin",
                  gradient: "from-destructive/20 to-destructive/5",
                  iconColor: "text-destructive"
                },
                {
                  title: "Deliveries", 
                  url: "/admin/deliveries",
                  icon: Package,
                  isActive: pathname === "/admin/deliveries",
                  gradient: "from-primary/20 to-primary/5",
                  iconColor: "text-primary"
                },
                {
                  title: "Customers",
                  url: "/admin/customers", 
                  icon: Users,
                  isActive: pathname === "/admin/customers",
                  gradient: "from-secondary/20 to-secondary/5",
                  iconColor: "text-secondary"
                },
                {
                  title: "Drivers",
                  url: "/admin/drivers",
                  icon: Truck,
                  isActive: pathname === "/admin/drivers",
                  gradient: "from-accent/20 to-accent/5",
                  iconColor: "text-accent"
                },
                {
                  title: "Fleet Management",
                  url: "/admin/fleet",
                  icon: Car,
                  isActive: pathname === "/admin/fleet",
                  gradient: "from-blue-500/20 to-blue-500/5",
                  iconColor: "text-blue-600"
                },
                {
                  title: "Vehicle Checker",
                  url: "/admin/vehicle-checker",
                  icon: Search,
                  isActive: pathname === "/admin/vehicle-checker",
                  gradient: "from-cyan-500/20 to-cyan-500/5",
                  iconColor: "text-cyan-600"
                },
                {
                  title: "Rota Management",
                  url: "/admin/rota",
                  icon: Calendar,
                  isActive: pathname === "/admin/rota",
                  gradient: "from-indigo-500/20 to-indigo-500/5",
                  iconColor: "text-indigo-600"
                },
                {
                  title: "Driver Timetable",
                  url: "/admin/timetable",
                  icon: CalendarDays,
                  isActive: pathname === "/admin/timetable",
                  gradient: "from-violet-500/20 to-violet-500/5",
                  iconColor: "text-violet-600"
                },
                {
                  title: "Expenses",
                  url: "/admin/expenses",
                  icon: Receipt,
                  isActive: pathname === "/admin/expenses",
                  gradient: "from-emerald-500/20 to-emerald-500/5",
                  iconColor: "text-emerald-600"
                },
                {
                  title: "Accounting",
                  url: "/accounting",
                  icon: Receipt,
                  isActive: pathname === "/accounting",
                  gradient: "from-green-500/20 to-green-500/5",
                  iconColor: "text-green-600"
                },
                {
                  title: "Performance",
                  url: "/performance",
                  icon: BarChart3,
                  isActive: pathname === "/performance",
                  gradient: "from-pink-500/20 to-pink-500/5",
                  iconColor: "text-pink-600"
                },
                {
                  title: "Incidents",
                  url: "/admin/incidents",
                  icon: AlertTriangle,
                  isActive: pathname === "/admin/incidents",
                  gradient: "from-warning/20 to-warning/5",
                  iconColor: "text-warning"
                },
                {
                  title: "Proof Uploads",
                  url: "/admin/proof-uploads",
                  icon: Camera,
                  isActive: pathname === "/admin/proof-uploads",
                  gradient: "from-blue-500/20 to-blue-500/5",
                  iconColor: "text-blue-600"
                },
                {
                  title: "Clock Records",
                  url: "/admin/clock-records",
                  icon: Clock,
                  isActive: pathname === "/admin/clock-records",
                  gradient: "from-emerald-500/20 to-emerald-500/5",
                  iconColor: "text-emerald-600"
                },
                {
                  title: "Documents",
                  url: "/admin/documents",
                  icon: FileText,
                  isActive: pathname === "/admin/documents",
                  gradient: "from-cyan-500/20 to-cyan-500/5",
                  iconColor: "text-cyan-600"
                },
                {
                  title: "Notifications",
                  url: "/admin/notifications",
                  icon: Bell,
                  isActive: pathname === "/admin/notifications",
                  gradient: "from-purple-500/20 to-purple-500/5",
                  iconColor: "text-purple-600"
                },
                {
                  title: "Activity Logs",
                  url: "/admin/activity-logs",
                  icon: History,
                  isActive: pathname === "/admin/activity-logs",
                  gradient: "from-violet-500/20 to-violet-500/5",
                  iconColor: "text-violet-600"
                },
                {
                  title: "Delivery Zones",
                  url: "/admin/delivery-zones",
                  icon: MapPin,
                  isActive: pathname === "/admin/delivery-zones",
                  gradient: "from-cyan-500/20 to-cyan-500/5",
                  iconColor: "text-cyan-600"
                },
                {
                  title: "Invoices",
                  url: "/admin/invoices",
                  icon: FileText,
                  isActive: pathname === "/admin/invoices",
                  gradient: "from-amber-500/20 to-amber-500/5",
                  iconColor: "text-amber-600"
                },
                {
                  title: "Location Tracking",
                  url: "/admin/location-tracking",
                  icon: Navigation,
                  isActive: pathname === "/admin/location-tracking",
                  gradient: "from-rose-500/20 to-rose-500/5",
                  iconColor: "text-rose-600"
                },
                {
                  title: "Live Driver Map",
                  url: "/admin/driver-map",
                  icon: Map,
                  isActive: pathname === "/admin/driver-map",
                  gradient: "from-blue-500/20 to-blue-500/5",
                  iconColor: "text-blue-600"
                },
                {
                  title: "Preset Locations",
                  url: "/admin/preset-locations",
                  icon: Map,
                  isActive: pathname === "/admin/preset-locations",
                  gradient: "from-teal-500/20 to-teal-500/5",
                  iconColor: "text-teal-600"
                },
                {
                  title: "Extras",
                  url: "/admin/extras",
                  icon: Cake,
                  isActive: pathname === "/admin/extras",
                  gradient: "from-orange-500/20 to-orange-500/5",
                  iconColor: "text-orange-600"
                },
                {
                  title: "Driver Alerts",
                  url: "/admin/driver-alerts",
                  icon: AlertTriangle,
                  isActive: pathname === "/admin/driver-alerts",
                  gradient: "from-warning/20 to-warning/5",
                  iconColor: "text-warning"
                },
                {
                  title: "Notes & Feedback",
                  url: "/admin/notes",
                  icon: StickyNote,
                  isActive: pathname === "/admin/notes",
                  gradient: "from-yellow-500/20 to-yellow-500/5",
                  iconColor: "text-yellow-600"
                },
                {
                  title: "Users",
                  url: "/admin/users",
                  icon: Users,
                  isActive: pathname === "/admin/users",
                  gradient: "from-indigo-500/20 to-indigo-500/5",
                  iconColor: "text-indigo-600"
                },
                {
                  title: "Reports",
                  url: "/admin/reports",
                  icon: FileText,
                  isActive: pathname === "/admin/reports",
                  gradient: "from-blue-500/20 to-blue-500/5",
                  iconColor: "text-blue-600"
                },
                {
                  title: "Recurring Bills",
                  url: "/admin/recurring-bills",
                  icon: Receipt,
                  isActive: pathname === "/admin/recurring-bills",
                  gradient: "from-emerald-500/20 to-emerald-500/5",
                  iconColor: "text-emerald-600"
                },
                {
                  title: "Company",
                  url: "/admin/company",
                  icon: Building2,
                  isActive: pathname === "/admin/company",
                  gradient: "from-slate-500/20 to-slate-500/5",
                  iconColor: "text-slate-600"
                },
                {
                  title: "Roles",
                  url: "/admin/roles",
                  icon: Shield,
                  isActive: pathname === "/admin/roles",
                  gradient: "from-red-500/20 to-red-500/5",
                  iconColor: "text-red-600"
                },
                {
                  title: "Analytics",
                  url: "/analytics",
                  icon: BarChart3,
                  isActive: pathname === "/analytics",
                  gradient: "from-success/20 to-success/5",
                  iconColor: "text-success"
                },
                {
                  title: "Settings", 
                  url: "/settings",
                  icon: Settings,
                  isActive: pathname === "/settings",
                  gradient: "from-muted-foreground/20 to-muted-foreground/5",
                  iconColor: "text-muted-foreground"
                },
              ].map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={item.isActive}
                    className={`
                      relative overflow-hidden transition-all duration-200 group
                      ${item.isActive 
                        ? `bg-gradient-to-r ${item.gradient} text-foreground border border-border/30 shadow-soft` 
                        : 'hover:bg-destructive/10 hover:text-accent-foreground'
                      }
                    `}
                  >
                    <NavLink 
                      to={item.url}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg relative z-10"
                    >
                      <div className={`transition-all duration-200 ${
                        item.isActive ? item.iconColor : 'text-muted-foreground group-hover:text-foreground'
                      }`}>
                        <item.icon className="h-5 w-5" />
                      </div>
                      <span className={`font-medium transition-all duration-200 ${
                        item.isActive ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'
                      }`}>
                        {item.title}
                      </span>
                      {item.isActive && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse"></div>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}